import Cookies from 'js-cookie';
import { useCallback } from 'react';
import { atom, useRecoilCallback, useRecoilState } from 'recoil';

export type SkinTone =
  | 'light'
  | 'mediumLight'
  | 'medium'
  | 'mediumDark'
  | 'dark';

/* An emoji modifier is a special character that can be used to change the skin tone of supported emoji.
 * An example of a modifier is "\u{1f3fb}" - adding this modifier right after a yellow emoji would turn ✋into ✋🏻.
 * */
export type SkinToneModifier =
  | '\u{1f3fb}'
  | '\u{1f3fc}'
  | '\u{1f3fd}'
  | '\u{1f3fe}'
  | '\u{1f3ff}';

export const skinToneMap: Record<SkinTone, SkinToneModifier> = {
  light: '\u{1f3fb}',
  mediumLight: '\u{1f3fc}',
  medium: '\u{1f3fd}',
  mediumDark: '\u{1f3fe}',
  dark: '\u{1f3ff}',
};

const COOKIE_KEY = 'emoji-skin-tone';

const modifiers = Object.values(skinToneMap);

const selectedSkinToneState = atom<SkinTone>({
  key: 'selected-skin-tone',
  default: null,
  effects: [
    /**
     * Synchronizes atom state with cookie.
     */
    ({ onSet, setSelf, trigger }) => {
      if (trigger === 'get') {
        const storedSkinTone = Cookies.get(COOKIE_KEY) as SkinTone;
        if (storedSkinTone) {
          setSelf(storedSkinTone);
        }
      }
      onSet((newSkinTone) => {
        if (!newSkinTone) {
          Cookies.remove(COOKIE_KEY, {
            sameSite: 'none',
            secure: true,
          });
        } else {
          Cookies.set(COOKIE_KEY, newSkinTone, {
            expires: new Date('Tue, 19 Jan 2038 03:14:07 GMT'),
            sameSite: 'none',
            secure: true,
          });
        }
      });
    },
  ],
});

export const useEmojiSkinTone = () => {
  const [currentSkinTone, selectSkinTone] = useRecoilState(
    selectedSkinToneState
  );

  const resetSkinTone = useRecoilCallback(
    ({ reset }) =>
      () => {
        reset(selectedSkinToneState);
      },
    []
  );

  /* Returns the current skin tone in the form of the modifier. */
  const getModifier = useCallback((tone: SkinTone) => {
    const modifier: SkinToneModifier = skinToneMap[tone];
    return modifier;
  }, []);

  /**
   * Returns a modified version of the passed base emoji.
   * Depends on currently selected skin tone.
   */
  const addModifier = useCallback(
    (emoji) => {
      /**
       * Not all emoji support modifiers - you can't change the skin tone of 😂, for example. Only emoji with the
       * Emoji_Modifier_Base property set to "Yes" can be modified. For more information about this property,
       * see http://unicode.org/reports/tr51/#Diversity_Implementations and https://stackoverflow.com/a/71912170
       */
      const supportsModifier = /\p{Emoji_Modifier_Base}/u.test(emoji);
      // can't support modifiers or no skin tone is set? return original emoji
      if (!supportsModifier || !currentSkinTone) return emoji;
      return `${emoji}${skinToneMap[currentSkinTone]}`;
    },
    [currentSkinTone]
  );

  /**
   * Removes the modifier, e.g. turns ✋🏻into ✋.
   */
  const removeModifier = useCallback((emoji) => {
    for (let i = 0; i < modifiers.length; i++) {
      emoji = emoji.replace(modifiers[i], '');
    }
    return emoji;
  }, []);

  return {
    currentSkinTone,
    addModifier,
    selectSkinTone,
    resetSkinTone,
    getModifier,
    removeModifier,
  };
};
